import type { MetricCardInstance } from '@diagon-alley/common';

/**
 * Home page — Control Metrics strip
 * 4 metric cards rendered at the top of Mission Control.
 */
export const HOME_PAGE_METRICS: MetricCardInstance[] = [
    // ── 1. Blended Days on Hand ─────────────────────────────────────────────
    {
        id: 'blended-days-on-hand',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'stock',
                endpoint: '/warehouse-stock',
                method: 'POST',
                params: { type: 'OVERALL' },
            },
        ],
        extractions: [
            { name: 'dohValue', from: 'stock', path: 'blended_days_on_hand' },
            { name: 'dohGap', from: 'stock', path: 'doh_gap' },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Blended Days on Hand' } },
            { fieldName: 'value', value: { type: 'ref', name: 'dohValue' } },
            {
                fieldName: 'subtext',
                value: {
                    type: 'computed',
                    fn: 'FORMAT',
                    args: [
                        { type: 'literal', value: '{{0}} days {{1}} than the target days on hand' },
                        { type: 'computed', fn: 'FIXED', args: [{ type: 'computed', fn: 'ABS', args: [{ type: 'ref', name: 'dohGap' }] }, { type: 'literal', value: 1 }] },
                        { type: 'computed', fn: 'SIGN_WORD', args: [{ type: 'ref', name: 'dohGap' }] },
                    ],
                },
            },
        ],
        format: { type: 'number', decimals: 1, suffix: ' days' },
        isEditable: false,
    },

    // ── 2. Daily Run Rate ────────────────────────────────────────────────────
    {
        id: 'daily-run-rate',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'sales',
                endpoint: '/sales/aggregate',
                method: 'POST',
                params: {
                    startDate: '{{yesterday}}',
                    endDate: '{{yesterday}}',
                    groupings: ['PRODUCT'],
                    isActive: true,
                },
            },
            {
                id: 'trend',
                endpoint: '/trend-calculation',
                method: 'POST',
                params: {
                    service_name: 'salesService',
                    method_name: 'getSalesAggregate',
                    method_params: {
                        start_date: '{{yesterday}}',
                        end_date: '{{yesterday}}',
                        groupings: ['PRODUCT'],
                        isActive: true,
                    },
                    evaluated_key: 'daily_run_rate',
                    comparison_period: 'SAME_DAY_LAST_WEEK',
                },
            },
        ],
        extractions: [
            { name: 'runRate', from: 'sales', path: 'daily_run_rate' },
            { name: 'trendPct', from: 'trend', path: 'trend' },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Daily Run Rate (in units)' } },
            { fieldName: 'value', value: { type: 'ref', name: 'runRate' } },
            {
                fieldName: 'trend',
                type: 'trend',
                valueRef: 'trendPct',
                positiveIsGood: true,
                labelTemplate: '{{absValue}}% {{direction}} vs same day last week',
            },
        ],
        format: { type: 'number', decimals: 1 },
        isEditable: false,
    },

    // ── 3. In-Transit (This Week) ────────────────────────────────────────────
    {
        id: 'in-transit-this-week',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'planning',
                endpoint: '/warehouse-stock/planning',
                method: 'POST',
                params: { periodType: 'WEEK', pageSize: 1000 },
            },
        ],
        extractions: [
            {
                name: 'totalInTransit',
                from: 'planning',
                path: 'data',
                field: 'plan[0].in_transit',
                aggregate: 'SUM',
            },
            {
                name: 'periodLabel',
                from: 'planning',
                path: 'data',
                field: 'plan[0].label',
                aggregate: 'FIRST',
            },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'In-Transit (This week)' } },
            { fieldName: 'value', value: { type: 'ref', name: 'totalInTransit' } },
            { fieldName: 'subtext', value: { type: 'ref', name: 'periodLabel' } },
        ],
        format: { type: 'number', suffix: ' units' },
        isEditable: false,
    },

    // ── 4. Open Sale Orders ──────────────────────────────────────────────────
    {
        id: 'open-sale-orders',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'OUTGOING',
                    // "Open" = not yet dispatched. Matches the definition used
                    // elsewhere in the codebase (purchase-order.service.ts).
                    status: ['Created', 'Scheduled'],
                    startDate: '{{daysAgo:180}}',
                    endDate: '{{today}}',
                    pageSize: 1000,
                },
            },
        ],
        extractions: [
            { name: 'orderCount', from: 'pos', path: 'total' },
            {
                name: 'topChannel',
                from: 'pos',
                path: 'data',
                field: 'channel',
                aggregate: 'MOST_FREQUENT',
            },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Open Sale Orders' } },
            { fieldName: 'value', value: { type: 'ref', name: 'orderCount' } },
            {
                fieldName: 'subtext',
                value: {
                    type: 'computed',
                    fn: 'FORMAT',
                    args: [
                        { type: 'literal', value: 'Channel with highest share: {{0}}' },
                        { type: 'ref', name: 'topChannel' },
                    ],
                },
            },
        ],
        format: { type: 'number' },
        isEditable: false,
    },
];
